/**
 * Shared portfolio calculations for dashboard and My Assets KPIs.
 */

import { formatCategoryDisplayName } from "@/utils/formatCategoryDisplayName";
import { distributeRoundedPercentages } from "@/utils/roundPercentages";

export interface CategoryValueInput {
  category: string;
  amount: number;
}

export interface PortfolioGrowthMetrics {
  growthPercent: number;
  growthAmount: number;
}

/**
 * Value-weighted diversification: 0% = single category, 100% = evenly spread.
 * Uses 1 - HHI (Herfindahl index).
 */
export function computeValueDiversificationPercent(categoryTotals: Record<string, number>): number {
  const entries = Object.values(categoryTotals).filter((v) => v > 0);
  const total = entries.reduce((sum, v) => sum + v, 0);
  if (total <= 0 || entries.length === 0) {
    return 0;
  }
  if (entries.length === 1) {
    return 0;
  }

  const hhi = entries.reduce((sum, value) => {
    const share = value / total;
    return sum + share * share;
  }, 0);

  const score = (1 - hhi) * 100;
  return Math.round(Math.min(100, Math.max(0, score)));
}

/**
 * Get Diversification Status Label.
 */
export function getDiversificationStatusLabel(score: number): {
  text: string;
  color: string;
} {
  if (score >= 80) {
    return { text: "Excellent", color: "text-green-600" };
  }
  if (score >= 60) {
    return { text: "Good", color: "text-blue-600" };
  }
  if (score >= 40) {
    return { text: "Moderate", color: "text-yellow-600" };
  }
  if (score > 0) {
    return { text: "Limited", color: "text-orange-600" };
  }
  return { text: "Not diversified", color: "text-orange-600" };
}

/** Count categories with positive total value (not enabled-preference slots). */
export function countCategoriesWithValue(categoryTotals: Record<string, number>): number {
  return Object.values(categoryTotals).filter((value) => value > 0).length;
}

/**
 * Estimated monthly growth from weighted asset returns (annual % → monthly amount).
 */
export function computePortfolioGrowth(
  totalValue: number,
  weightedAnnualReturnPercent: number
): PortfolioGrowthMetrics {
  if (totalValue <= 0 || weightedAnnualReturnPercent <= 0) {
    return { growthPercent: 0, growthAmount: 0 };
  }
  const growthPercent = weightedAnnualReturnPercent / 12;
  const growthAmount = Math.round((totalValue * growthPercent) / 100);
  return { growthPercent: Math.round(growthPercent * 10) / 10, growthAmount };
}

/**
 * Portfolio return % from weighted annual returns (same basis as growth KPI).
 */
export function computePortfolioReturnPercent(weightedAnnualReturnPercent: number): number {
  if (weightedAnnualReturnPercent <= 0) {
    return 0;
  }
  return Math.round((weightedAnnualReturnPercent / 12) * 10) / 10;
}

export function buildPortfolioLegendCategories(
  categoryValueTotals: Record<string, number>,
  categoryBreakdown: Record<string, number>
): Array<readonly [string, number]> {
  return Object.entries(categoryValueTotals)
    .filter(([, value]) => value > 0)
    .sort(([, a], [, b]) => Number(b) - Number(a))
    .map(([category]) => [category, categoryBreakdown[category] || 0] as const);
}

/**
 * Build Category Totals From Assets.
 */
export function buildCategoryTotalsFromAssets(
  assets: CategoryValueInput[]
): Record<string, number> {
  return assets.reduce<Record<string, number>>((acc, asset) => {
    const category = asset.category || "Other";
    const amount = Number(asset.amount) || 0;
    if (amount <= 0) {
      return acc;
    }
    acc[category] = (acc[category] || 0) + amount;
    return acc;
  }, {});
}

export interface AssetCategoryAllocationRow {
  name: string;
  displayName: string;
  value: number;
  percentage: number;
}

/** Category allocation rows with integer percentages that sum to exactly 100%. */
export function buildAssetCategoryAllocationRows(
  categoryTotals: Record<string, number>
): AssetCategoryAllocationRow[] {
  const entries = Object.entries(categoryTotals)
    .filter(([, value]) => value > 0)
    .sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return [];
  }

  const amounts = entries.map(([, value]) => value);
  const percentages = distributeRoundedPercentages(amounts);

  return entries.map(([name, value], index) => ({
    name,
    displayName: formatCategoryDisplayName(name),
    value,
    percentage: percentages[index] ?? 0,
  }));
}

/**
 * Compute Weighted Annual Return.
 */
export function computeWeightedAnnualReturn(
  assets: Array<{ amount: number; returns?: number }>,
  defaultReturn = 8.5
): number {
  const total = assets.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  if (total <= 0) {
    return 0;
  }
  return assets.reduce((sum, asset) => {
    const amount = Number(asset.amount) || 0;
    if (amount <= 0) {
      return sum;
    }
    const weight = amount / total;
    const returns =
      typeof asset.returns === "number" && !Number.isNaN(asset.returns)
        ? asset.returns
        : defaultReturn;
    return sum + weight * returns;
  }, 0);
}
